import React from 'react';
import PropTypes from 'prop-types';
import formatNumber from '../../../utils/format-number';
import { isTruthyLimitValue } from '../../../utils/check-limits';
import { SUBDIRECTORY_ADDON_NAME, SUBDIRECTORY_IDENTIFIER } from '../../../utils/constants';

/**
 * @typedef {object} AddOnItem
 * @property {string} name
 * @property {number} cost
 * @property {{name?: string, cost?: number}[]} [prices]
 */

/**
 * @typedef {import('../../../types').Limit} Limit
 * @typedef {Pick<import('../../../types').CurrentPrice, 'base_product'|'cost'>} CurrentPrice
 */

/**
 * @typedef {object} AddOnsProps
 * @property {CurrentPrice} currentPrice
 * @property {AddOnItem[]} addonItems
 * @property {Limit[]} subscriptionLimits
 * @property {number} baseProductQuantity
 */

/**
 * @param {AddOnItem} addon
 * @returns {JSX.Element|null}
 */
function AddOnItem({ addon }) {
    const name = addon?.prices?.[0]?.name ?? addon?.name;
    const cost = addon?.prices?.[0]?.cost ?? addon?.cost;
    const displayName = isSubdirectoryAddon(addon) ? SUBDIRECTORY_ADDON_NAME : name;

    return (
        <div className="invoices-item">
            <div className="invoice-desc">{displayName}</div>
            <div className="invoice-price">{formatNumber(cost, { symbol: '$' })}</div>
        </div>
    );
}

AddOnItem.propTypes = {
    addon: PropTypes.shape({
        name: PropTypes.string,
        cost: PropTypes.number,
        prices: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                cost: PropTypes.number,
            }),
        ),
    }).isRequired,
};

/**
 * @param {AddOnItem} addon
 * @returns {boolean}
 */
function isSubdirectoryAddon(addon) {
    const name = addon?.prices?.[0]?.name ?? addon?.name;
    return name?.toLowerCase().includes(SUBDIRECTORY_IDENTIFIER) === true;
}

/**
 * @param {AddOnsProps} props
 * @returns {JSX.Element}
 */
export default function AddOns({
    currentPrice,
    addonItems = [],
    subscriptionLimits = [],
    baseProductQuantity = 1,
}) {
    const hasSubdirectoryAddon = addonItems.some(isSubdirectoryAddon);
    const isSubdirectoryIncludedInSubscription =
        !hasSubdirectoryAddon &&
        subscriptionLimits.some(
            (limit) => limit.name === SUBDIRECTORY_IDENTIFIER && isTruthyLimitValue(limit.value),
        );

    if (!addonItems.length && !isSubdirectoryIncludedInSubscription) {
        return null;
    }

    return (
        <div className="plandetails-breakdown">
            <div className="staticfield-label">Price breakdown</div>
            <div className="invoices-item">
                <div className="invoice-desc">{currentPrice.base_product} plan</div>
                <div className="invoice-price">
                    {formatNumber(currentPrice.cost * baseProductQuantity, { symbol: '$' })}
                </div>
            </div>

            {addonItems.map(({ ...addon }) => (
                <AddOnItem key={addon.name} addon={addon} />
            ))}

            {isSubdirectoryIncludedInSubscription && (
                <div className="invoices-item">
                    <div className="invoice-desc">{SUBDIRECTORY_ADDON_NAME} (incl. in plan)</div>
                </div>
            )}
        </div>
    );
}

AddOns.propTypes = {
    currentPrice: PropTypes.shape({
        base_product: PropTypes.string,
        cost: PropTypes.number,
    }).isRequired,
    addonItems: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            cost: PropTypes.number,
            prices: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    cost: PropTypes.number,
                }),
            ),
        }),
    ),
    subscriptionLimits: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
        }),
    ),
    baseProductQuantity: PropTypes.number,
};
